/**
 * Il testo dell'assistente, pronto per il Markdown.
 *
 * Il modello scrive in Markdown (grassetti, elenchi, tabelle), che la chat
 * rende senza mai rendere l'HTML scritto dal modello. Indica le viste
 * scrivendo il percorso che gli e arrivato dalle funzioni, a volte dentro un
 * link: qui il percorso nudo diventa un link con il titolo del catalogo, cosi
 * in chat compare un pulsante e non l'indirizzo, e solo i percorsi
 * dell'applicazione restano collegamenti.
 */

import { VISTE_ASSISTENTE, contestoDaPercorso } from '@calcio/shared';

/** Le radici delle rotte: un percorso fuori da queste non diventa un link. */
const RADICI = ['sq', 'giocatori', 'competizioni', 'amministrazione', 'assistente'];

// Il percorso vale solo a inizio parola: dentro `/it/app/giocatori`, che il
// modello a volte inventa, `/giocatori` non e una nostra rotta.
const PERCORSO = `(?<![\\w/.])/(?:${RADICI.join('|')})(?:/[\\w-]+)*`;
const RICONOSCI = new RegExp(`\\[([^\\]\\n]+)\\]\\((${PERCORSO})\\)|(${PERCORSO})`, 'g');
const SOLO_PERCORSO = new RegExp(`^${PERCORSO}$`);

function titoloVista(percorso: string): string {
  const vista = contestoDaPercorso(percorso).vista;
  return vista ? VISTE_ASSISTENTE[vista.chiave].titolo : 'Apri';
}

/** Il testo con i percorsi nudi dell'applicazione scritti come link. */
export function conCollegamenti(testo: string): string {
  return testo.replace(RICONOSCI, (intero, _scritta, _delLink, nudo: string | undefined) =>
    nudo ? `[${titoloVista(nudo)}](${nudo})` : intero,
  );
}

/** Il percorso di un link se e una rotta dell'applicazione, altrimenti null. */
export function percorsoApp(href: string | undefined): string | null {
  return href && SOLO_PERCORSO.test(href) ? href : null;
}

/** Le operazioni la cui descrizione non porta già il risultato. */
const CON_RISULTATO = [
  'somma',
  'media',
  'mediana',
  'conteggio',
  'differenza',
  'percentuale',
  'rapporto',
];

/**
 * L'intestazione di una lettura sotto la risposta: il nome della funzione e
 * «dati letti», o l'errore; per un calcolo «Calcolo» e il conto fatto, così si
 * vede senza aprire da dove viene un numero.
 */
export function intestazioneLettura(
  funzione: string | undefined,
  dati: unknown,
): { nome: string; sintesi: string; calcolo: boolean } {
  const d = (dati ?? {}) as {
    errore?: string;
    operazione?: string;
    descrizione?: string;
    risultato?: number;
  };
  const calcolo = funzione === 'calcola';
  if (calcolo && !d.errore && d.descrizione) {
    const conRisultato = d.risultato !== undefined && CON_RISULTATO.includes(d.operazione ?? '');
    const valore = conRisultato
      ? ` = ${d.risultato!.toLocaleString('it-IT')}${d.operazione === 'percentuale' ? '%' : ''}`
      : '';
    return { nome: 'Calcolo', sintesi: `${d.descrizione}${valore}`, calcolo };
  }
  const nome = (funzione ?? 'lettura').replaceAll('_', ' ');
  return {
    nome: calcolo ? 'Calcolo' : nome.charAt(0).toUpperCase() + nome.slice(1),
    sintesi: d.errore ?? 'dati letti',
    calcolo,
  };
}

/**
 * Il testo dell'avviso sotto una risposta con numeri che non vengono da una
 * lettura né da un calcolo (il controllo lo fa il server).
 */
export function avvisoNumeri(numeri: readonly string[]): string {
  if (numeri.length === 1) {
    return `Il numero ${numeri[0]} non viene da una lettura né da un calcolo: controllalo nei dati letti.`;
  }
  const elenco = `${numeri.slice(0, -1).join(', ')} e ${numeri.at(-1)}`;
  return `I numeri ${elenco} non vengono da una lettura né da un calcolo: controllali nei dati letti.`;
}
